import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useDependencyContext } from './DependencyContext';

export function useItemPositionTracking({
  itemId,
  itemRef,
  hasDependencies,
  resizeWidth,
}: {
  itemId: number;
  itemRef: React.RefObject<HTMLDivElement | null>;
  hasDependencies: boolean;
  resizeWidth?: number;
}) {
  const {
    registerElement,
    unregisterElement,
    trackItemForRendering,
    untrackItemForRendering,
    hoveredItemId,
    dragState,
    setHoveredItem,
    includeDependencies,
  } = useDependencyContext();

  const [isLocalHovered, setIsLocalHovered] = useState(false);
  const isHovered = hoveredItemId === itemId;
  const isDragTarget = dragState.target?.itemId === itemId;
  const isDragSource = dragState.source?.itemId === itemId;
  const shouldTrack =
    includeDependencies &&
    (hasDependencies || isHovered || isDragTarget || isDragSource || isLocalHovered);

  // biome-ignore lint/correctness/useExhaustiveDependencies: resizeWidth triggers re-registration when the item's width changes; other deps are stable context values
  useLayoutEffect(() => {
    if (!shouldTrack || !itemRef.current) {
      return;
    }
    registerElement(itemId, itemRef.current);
    trackItemForRendering(itemId);

    return () => {
      unregisterElement(itemId);
      untrackItemForRendering(itemId);
    };
  }, [shouldTrack, itemId, resizeWidth]);

  const handleMouseEnter = useCallback(() => {
    if (!includeDependencies) {
      return;
    }
    setIsLocalHovered(true);
    setHoveredItem(itemId);

    if (itemRef.current) {
      registerElement(itemId, itemRef.current);
      trackItemForRendering(itemId);
    }
  }, [
    includeDependencies,
    itemId,
    itemRef,
    registerElement,
    trackItemForRendering,
    setHoveredItem,
  ]);

  const handleMouseLeave = useCallback(() => {
    if (!includeDependencies) {
      return;
    }
    setIsLocalHovered(false);
    setHoveredItem(null);
  }, [includeDependencies, setHoveredItem]);

  const props = useMemo(() => {
    return {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      isHovered,
    };
  }, [handleMouseEnter, handleMouseLeave, isHovered]);

  return props;
}
